import lastDayOfMonth from 'date-fns/lastDayOfMonth'
import { assertNever } from './assertNever'
import { TimeRange, Timespan } from './timeRange'
import { Nullable } from './type-utils'

export enum IntervalUnit {
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
  Years = 'years',
}

export interface Interval {
  unit: IntervalUnit
  value: number
}

const UnitTable: Map<string, IntervalUnit> = new Map([
  ['minute', IntervalUnit.Minutes],
  ['minutes', IntervalUnit.Minutes],
  ['hour', IntervalUnit.Hours],
  ['hours', IntervalUnit.Hours],
  ['day', IntervalUnit.Days],
  ['days', IntervalUnit.Days],
  ['week', IntervalUnit.Weeks],
  ['weeks', IntervalUnit.Weeks],
  ['month', IntervalUnit.Months],
  ['months', IntervalUnit.Months],
  ['year', IntervalUnit.Years],
  ['years', IntervalUnit.Years],
])

export enum RelativeToType {
  Before = 'before',
  After = 'after',
}

export function normalizeIntervalUnit(unit: string): IntervalUnit {
  const normalized = UnitTable.get(unit)
  if (normalized === undefined) {
    throw new Error(`invalid interval unit \`${unit}\``)
  }
  return normalized
}

export function calculateAge(birthday: Date, dateContext: number = Date.now()) {
  const currentDate = new Date(dateContext)
  const age = currentDate.getFullYear() - birthday.getFullYear()
  const month = currentDate.getMonth() - birthday.getMonth()

  return month < 0 || (month === 0 && currentDate.getDate() < birthday.getDate()) ? age - 1 : age
}

export function dateToSQLDate(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

export function dateToSQLMonthAndDay(date: Date): string {
  return [String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')
}

function hms(date: Date) {
  return [
    String(date.getHours()).padStart(2, '0'),
    String(date.getMinutes()).padStart(2, '0'),
    String(date.getSeconds()).padStart(2, '0'),
  ].join(':')
}

export function dateToSQLDateTime(date: Date): string {
  return `${dateToSQLDate(date)} ${hms(date)}`
}

export function dateToApiDateTime(date: Date): string {
  return `${dateToSQLDate(date)}T${hms(date).slice(0, 5)}`
}

export function formatRelativeTime({ value, unit }: Nullable<Interval>): string | null {
  if (!value || !unit) {
    return null
  }
  // just to be safe we ensure unit is converted to enum type as sometimes other unit strings are used in db
  unit = normalizeIntervalUnit(unit)

  let hours
  let minutes = 0
  switch (unit) {
    case IntervalUnit.Minutes:
      minutes = value % 60
      hours = Math.round((value - minutes) / 60)
      break
    case IntervalUnit.Hours:
      hours = value
      break
    case IntervalUnit.Days:
      hours = value * 24
      break
    case IntervalUnit.Weeks:
      hours = value * 24 * 7
      break
    // INFO: Of course this is just an approximation (28st of February - 1 Month = ???) --> But this is also used in
    // the session validation function so this should be good
    case IntervalUnit.Months:
      hours = value * 24 * 30
      break
    // INFO: Of course this is just a stupid approximation (leap year) --> But this is also used in
    // the session validation function so this should be good
    case IntervalUnit.Years:
      hours = value * 24 * 365
      break
    default:
      assertNever(unit)
  }

  // add leading 0 to single digit minutes
  const formattedMinutes = `0${minutes}`.slice(-2)
  return `${hours}:${formattedMinutes}`
}

/**
 * Sets a Date to the beginning of the week (monday)
 */
export function setDateToStartOfWeek(date: Date): Date {
  // 0 = sunday, 1 = monday ... 6 = saturday
  const weekDay = date.getDay()
  const daysAfterMonday = weekDay === 0 ? 6 : weekDay - 1
  date.setDate(date.getDate() - daysAfterMonday)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Sets a Date to the beginning of the month
 */
export function setDateToStartOfMonth(date: Date): Date {
  date.setDate(1)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Sets a Date to the beginning of the year
 */
export function getStartOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1)
}

/**
 * Get's the timerange of the week within a date lies
 */
export function getWeekTimeRange(date: Date): Timespan {
  const from = setDateToStartOfWeek(new Date(date))
  const until = addInterval(from, { value: 1, unit: IntervalUnit.Weeks })
  return {
    from,
    until,
  }
}

/**
 * Get's the timerange of the month within a date lies
 */
export function getMonthTimeRange(date: Date): Timespan {
  const from = setDateToStartOfMonth(new Date(date))
  const until = addInterval(from, { value: 1, unit: IntervalUnit.Months })
  return {
    from,
    until,
  }
}

/**
 * Get's the timerange of the year within a date lies
 */
export function getYearTimeRange(date: Date): Timespan {
  const from = getStartOfYear(date)
  const until = addInterval(from, { value: 1, unit: IntervalUnit.Years })
  return {
    from,
    until,
  }
}

/**
 * Get duration in minutes between two dates
 */
export function getDurationBetweenDates(startDate: Date, endDate: Date): number {
  return (endDate.getTime() - startDate.getTime()) / 1000 / 60
}

/**
 * Add an interval to a date
 */
export function addInterval(date: Date, interval: Interval): Date {
  return getDateRelativeFromOtherDate(interval, RelativeToType.After, date)
}

/**
 * Subtract an interval from a date
 *
 * @deprecated Please use @eversports\/dates\/sub instead
 */
export function subtractInterval(date: Date, interval: Interval): Date {
  return getDateRelativeFromOtherDate(interval, RelativeToType.Before, date)
}

/**
 * Returns a new date x units before or after a given Date
 */
export function getDateRelativeFromOtherDate({ value, unit }: Interval, type: RelativeToType, date: Date): Date {
  // just to be safe we ensure unit is converted to enum type as sometimes other unit strings are used in db
  unit = normalizeIntervalUnit(unit)

  // clone date object, we don't want to alter the passed one
  const newDate = new Date(date)

  // flip value depending on whether we want to add or subtract units
  value *= type === RelativeToType.After ? 1 : -1

  switch (unit) {
    case IntervalUnit.Minutes:
      newDate.setHours(newDate.getHours(), newDate.getMinutes() + value)
      break
    case IntervalUnit.Hours:
      newDate.setHours(newDate.getHours() + value)
      break
    case IntervalUnit.Days:
      newDate.setDate(newDate.getDate() + value)
      break
    case IntervalUnit.Weeks:
      newDate.setDate(newDate.getDate() + value * 7)
      break
    case IntervalUnit.Months:
      newDate.setMonth(newDate.getMonth() + value)
      break
    case IntervalUnit.Years:
      newDate.setFullYear(newDate.getFullYear() + value)
      break
  }

  return newDate
}

/**
 * Checks if a target date or interval falls within a specified timerange.
 * Target timerange is interpreted as OPEN interval, meaning it does NOT include its boundary conditions
 * Timerange is interpreted as CLOSED interval, meaning it DOES include its boundary conditions
 *
 * Target timerange is considered "in" timerange if there is any overlap
 * Target timerange 14-16h is NOT included in 16h-20h timeRange (for example user can book until 16h if courtblock starts at 16h)
 * Target Date 16h however IS included in 16h-20h timeRange (for example user can book at 16h if product validity starts at 16h)
 */
export function isTargetInTimeRange(target: TimeRange | Date, timeRange: Nullable<TimeRange>) {
  const start = timeRange.start || minDate()
  const end = timeRange.end || maxDate()

  if (target instanceof Date) {
    return target >= start && target <= end
  }
  return target.end > start && target.start < end
}

export function getTimeRangeFromTimeRangeList(timeRanges: Array<TimeRange>): TimeRange {
  if (timeRanges.length === 0) {
    throw new Error('at least one time range is expected')
  }

  return {
    start: new Date(Math.min(...timeRanges.map(({ start }) => start.getTime()))),
    end: new Date(Math.max(...timeRanges.map(({ end }) => end.getTime()))),
  }
}

export function isInFuture(target: TimeRange | Date) {
  return now() <= (target instanceof Date ? target : target.start)
}

export function isValidTime(timeString: string): boolean {
  return /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])(:[0-5][0-9])?$/.test(timeString)
}

/**
 * Returns ~275.000 A.D.
 */
export function maxDate(): Date {
  return new Date(8640000000000000)
}

/**
 * Returns ~270.000 B.C.
 */
export function minDate(): Date {
  return new Date(-8640000000000000)
}

export function getMaxDate(...dates: Array<Date>): Date {
  return dates.reduce((max, date) => (date.getTime() > max.getTime() ? date : max))
}

export function now(): Date {
  return new Date()
}

// Date matches YYYY-MM-DD format
export function matchesYYYYMMDDFormat(date: string) {
  return /^(19[0-9]{2}|2[0-9]{3})-(0[1-9]|1[012])-([123]0|[012][1-9]|31)$/.test(date)
}

/**
 * @deprecated Use @eversports\/dates/getEndOfMonth instead
 */
export function getEndOfMonth(date: Date) {
  return lastDayOfMonth(date)
}

export function addBusinessDays(date: Date, numDaysToAdd: number): Date {
  let daysToAdd = numDaysToAdd
  const newDate = new Date(date.getTime())
  while (daysToAdd > 0) {
    newDate.setDate(newDate.getDate() + 1)
    if (![0, 6].includes(newDate.getDay())) {
      daysToAdd--
    }
  }
  return newDate
}

export function isSameDay(d1: Date, d2: Date) {
  return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear()
}

export function isToday(date: Date) {
  const today = new Date()
  return (
    today.getDate() === date.getDate() &&
    today.getMonth() === date.getMonth() &&
    today.getFullYear() === date.getFullYear()
  )
}

export function isTomorrow(date: Date) {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return (
    tomorrow.getDate() === date.getDate() &&
    tomorrow.getMonth() === date.getMonth() &&
    tomorrow.getFullYear() === date.getFullYear()
  )
}

export function getMonthsBetweenDates(startDate: Date, endDate: Date): number {
  return Math.max((endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth(), 0)
}

export function getWeeksBetweenDates(startDate: Date, endDate: Date): number {
  let diff = (endDate.getTime() - startDate.getTime()) / 1000
  diff /= 60 * 60 * 24 * 7
  return Math.abs(Math.round(diff))
}

export function validateAndParseDateString(str: string): Date {
  if (!str || isNaN(new Date(str).getTime())) throw Error('invalid Date')
  return new Date(str)
}
